const PLAN_INTERVAL = {
  MONTH: 'month',
  YEAR: 'year',
  TRIAL: '7_day',
};

const APPLICABILITY = {
  ALL: 'All',
  ANNUAL: 'Annual',
  MONTHLY: 'Monthly',
};

export const normalizeCouponCode = value => {
  if (value === null || value === undefined) return '';
  return String(value).trim().toUpperCase();
};

export const planLabelFromKey = value => {
  if (!value) return null;
  const key = String(value).toLowerCase();
  if (key === 'year' || key === 'annual') return APPLICABILITY.ANNUAL;
  if (key === 'month' || key === 'monthly') return APPLICABILITY.MONTHLY;
  if (key === 'all') return APPLICABILITY.ALL;
  return null;
};

export const detectCouponApplicability = coupon => {
  const metadata = coupon?.metadata || {};
  const rawApplicability =
    metadata.applicable_to ||
    metadata.plan ||
    metadata.interval ||
    metadata.valid_for ||
    metadata.applies_to ||
    null;

  return planLabelFromKey(rawApplicability) || APPLICABILITY.ALL;
};

export const isAnnualOnlyCoupon = ({ coupon, couponCode, annualCouponCodes = [] } = {}) => {
  const normalizedCode =
    normalizeCouponCode(couponCode) ||
    normalizeCouponCode(coupon?.code) ||
    normalizeCouponCode(coupon?.name) ||
    normalizeCouponCode(coupon?.id);

  if (!normalizedCode || !Array.isArray(annualCouponCodes)) return false;
  return annualCouponCodes.map(normalizeCouponCode).includes(normalizedCode);
};

export const isCouponApplicableToInterval = ({
  coupon,
  interval,
  couponCode,
  annualCouponCodes = [],
} = {}) => {
  if (!interval || interval === PLAN_INTERVAL.TRIAL) return false;

  const isAnnualOnly = isAnnualOnlyCoupon({ coupon, couponCode, annualCouponCodes });
  if (isAnnualOnly) {
    return interval === PLAN_INTERVAL.YEAR;
  }

  const applicability = detectCouponApplicability(coupon);
  if (applicability === APPLICABILITY.ALL) {
    return interval === PLAN_INTERVAL.MONTH || interval === PLAN_INTERVAL.YEAR;
  }
  if (applicability === APPLICABILITY.ANNUAL) {
    return interval === PLAN_INTERVAL.YEAR;
  }
  if (applicability === APPLICABILITY.MONTHLY) {
    return interval === PLAN_INTERVAL.MONTH;
  }

  return false;
};

export const calculateDiscountedAmount = ({ unitAmount, coupon } = {}) => {
  if (typeof unitAmount !== 'number' || unitAmount < 0) {
    return null;
  }

  const percentOff =
    typeof coupon?.percent_off === 'number' && coupon.percent_off > 0 ? coupon.percent_off : null;
  const amountOff =
    typeof coupon?.amount_off === 'number' && coupon.amount_off > 0 ? coupon.amount_off : null;

  if (percentOff === null && amountOff === null) return unitAmount;

  if (percentOff !== null) {
    const discountAmount = Math.round((unitAmount * percentOff) / 100);
    return Math.max(unitAmount - discountAmount, 0);
  }

  return Math.max(unitAmount - amountOff, 0);
};

export const resolveSignupPricing = ({
  plan,
  coupon,
  couponCode,
  annualCouponCodes = [],
  couponValidated = false,
} = {}) => {
  const interval = plan?.recurring_interval || null;
  const unitAmount = typeof plan?.unit_amount === 'number' ? plan.unit_amount : null;
  const hasCoupon = Boolean(coupon);

  const annualOnly = hasCoupon && isAnnualOnlyCoupon({ coupon, couponCode, annualCouponCodes });
  const couponApplicability = hasCoupon ? detectCouponApplicability(coupon) : APPLICABILITY.ALL;
  const couponAppliesToPlan =
    hasCoupon &&
    isCouponApplicableToInterval({
      coupon,
      interval,
      couponCode,
      annualCouponCodes,
    });

  const discountedUnitAmount =
    couponAppliesToPlan && unitAmount !== null
      ? calculateDiscountedAmount({ unitAmount, coupon })
      : unitAmount;

  const isPercentFree = couponAppliesToPlan && coupon?.percent_off === 100;
  const isAmountFree =
    couponAppliesToPlan &&
    typeof coupon?.amount_off === 'number' &&
    typeof unitAmount === 'number' &&
    unitAmount > 0 &&
    coupon.amount_off >= unitAmount;

  const isFree = Boolean(
    couponAppliesToPlan && (isPercentFree || isAmountFree || discountedUnitAmount === 0)
  );
  const requiresPaymentMethod = !couponValidated || !couponAppliesToPlan || !isFree;

  return {
    interval,
    unitAmount,
    hasCoupon,
    annualOnly,
    couponApplicability,
    couponAppliesToPlan,
    discountedUnitAmount,
    isFree,
    requiresPaymentMethod,
    enforcedPlanInterval: annualOnly ? PLAN_INTERVAL.YEAR : null,
    shouldDisplayTrial: interval === PLAN_INTERVAL.TRIAL && !isFree,
  };
};

export const signupPlanIntervals = PLAN_INTERVAL;
export const couponApplicabilityTypes = APPLICABILITY;
